<template>
  <div class="view-on-mobile">
    <div
      ref="qrPillRef"
      class="pill"
      :class="{ 'pill--open': qrOpen }"
      role="button"
      :aria-expanded="qrOpen"
      @mouseenter="onQrMouseEnter"
      @mouseleave="onQrMouseLeave"
      @touchstart.passive="onQrTouchStart"
    >
      <div
        class="pill__icons"
        aria-hidden="true"
      >
        <img
          src="@/assets/images/viewOnMobile/preview.png"
          class="pill-icon-img"
          :alt="t('common.viewOnMobileAlt')"
        >
      </div>
      <span class="pill__label">{{ t('common.viewOnMobile') }}</span>

      <!-- PC：面板留在 pill 内，mouseleave 可覆盖面板区域 -->
      <div
        v-if="!isTouchMode"
        class="mobile-qr-panel"
        :class="{ 'mobile-qr-panel--visible': qrOpen }"
        :aria-label="t('common.viewOnMobile')"
      >
        <MobileQrPanelContent />
      </div>
    </div>

    <div class="pill pill--lang" v-show="false">
      <div
        class="pill__icons"
        aria-hidden="true"
      >
        <img
          src="@/assets/images/viewOnMobile/lang.png"
          class="pill-icon-img"
          alt=""
        >
      </div>
      <span class="pill__label">{{ currentLangLabel }}</span>
      <div class="pill-chevron-wrapper">
        <img
          src="@/assets/images/viewOnMobile/chevron-down.png"
          class="pill-chevron"
          alt=""
        >
      </div>
      <div class="lang-pill-wrapper-mask">
        <div
          class="lang-pill-wrapper"
          role="menu"
          :aria-label="t('common.chinese') + ' / ' + t('common.english')"
        >
          <button
            type="button"
            class="lang-pill-wrapper__row lang-pill-wrapper__row--top"
            :class="{ 'lang-pill-wrapper__row--active': locale === 'zh-CN' }"
            role="menuitemradio"
            :aria-checked="locale === 'zh-CN'"
            @click="changeLang('/zh-CN')"
          >
            <span class="lang-pill-wrapper__label">{{ t('common.chinese') }}</span>
          </button>
          <button
            type="button"
            class="lang-pill-wrapper__row lang-pill-wrapper__row--bottom"
            :class="{ 'lang-pill-wrapper__row--active': locale === 'en-US' }"
            role="menuitemradio"
            :aria-checked="locale === 'en-US'"
            @click="changeLang('/en-US')"
          >
            <span class="lang-pill-wrapper__label">{{ t('common.english') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 移动端：全屏遮罩 + fixed 面板，避免被 Header 层叠上下文遮挡 -->
  <Teleport to="body">
    <div
      v-if="qrOpen && isTouchMode"
      class="mobile-qr-layer"
    >
      <div
        class="mobile-qr-overlay"
        aria-hidden="true"
        @click="onOverlayClick"
        @touchstart="onOverlayTouchStart"
        @touchend="onOverlayTouchEnd"
      />
      <div
        ref="qrPanelRef"
        class="mobile-qr-panel mobile-qr-panel--fixed mobile-qr-panel--visible"
        :style="panelFixedStyle"
        :aria-label="t('common.viewOnMobile')"
        @click.stop
        @touchstart.stop
        @touchend.stop
        @touchmove.stop
      >
        <MobileQrPanelContent />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { findBuryKey } from '#imports'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import MobileQrPanelContent from '~/components/NavHeader/MobileQrPanelContent.vue'
import { safeStorageSetItem } from '~/utils/safeLocalStorage'

const emit = defineEmits<{
  'panel-hover-change': [expanded: boolean]
}>()

const { locale, t } = useI18n()
const route = useRoute()

const qrPillRef = ref<HTMLElement | null>(null)
const qrPanelRef = ref<HTMLElement | null>(null)
const qrOpen = ref(false)
const isTouchMode = ref(false)
const panelFixedStyle = ref<Record<string, string>>({})

/** pill 顶部到面板顶部的间距，与 CSS top: 59px 一致 */
const PANEL_TOP_OFFSET = 59

/** touch 后短暂忽略 mouse 事件，避免合成 hover 干扰 */
let suppressMouseUntil = 0
/** 打开后忽略遮罩关闭，避免抬手时的合成 click 误触关闭 */
let ignoreOverlayCloseUntil = 0

const currentLangLabel = computed(() =>
  locale.value === 'en-US' ? t('common.english') : t('common.chinese'),
)

const shouldIgnoreMouse = () => Date.now() < suppressMouseUntil

const closeQr = () => {
  qrOpen.value = false
}

const setQrOpen = (open: boolean) => {
  qrOpen.value = open
}

const onOverlayClick = (e: MouseEvent) => {
  if (Date.now() < ignoreOverlayCloseUntil) return
  // 触摸设备上由 touchend 关闭，避免与合成 click 重复且防止穿透
  if (isTouchMode.value) {
    e.preventDefault()
    return
  }
  closeQr()
}

/** touchstart 仅拦截默认行为，不在此关闭（避免遮罩提前消失导致 click 穿透） */
const onOverlayTouchStart = (e: TouchEvent) => {
  if (Date.now() < ignoreOverlayCloseUntil) {
    e.preventDefault()
    return
  }
  e.preventDefault()
}

const onOverlayTouchEnd = (e: TouchEvent) => {
  if (Date.now() < ignoreOverlayCloseUntil) return
  e.preventDefault()
  closeQr()
}

const updatePanelPosition = () => {
  const el = qrPillRef.value
  if (!el || !isTouchMode.value) return

  const rect = el.getBoundingClientRect()
  const isMobileLayout = window.innerWidth <= 767
  const rightExtend = isMobileLayout ? 30 : 0

  panelFixedStyle.value = {
    top: `${rect.top + PANEL_TOP_OFFSET}px`,
    right: `${window.innerWidth - rect.right - rightExtend}px`,
  }
}

const onQrMouseEnter = () => {
  if (shouldIgnoreMouse() || isTouchMode.value) return
  setQrOpen(true)
}

const onQrMouseLeave = () => {
  if (shouldIgnoreMouse() || isTouchMode.value) return
  closeQr()
}

const onQrTouchStart = () => {
  suppressMouseUntil = Date.now() + 500
  isTouchMode.value = true
  if (qrOpen.value) return
  setQrOpen(true)
  ignoreOverlayCloseUntil = Date.now() + 500
  nextTick(updatePanelPosition)
}

const detectTouchMode = () => {
  isTouchMode.value = true
}

const onResize = () => {
  if (qrOpen.value && isTouchMode.value) {
    updatePanelPosition()
  }
}

watch(qrOpen, (open) => {
  emit('panel-hover-change', open)
  if (open && isTouchMode.value) {
    nextTick(updatePanelPosition)
  }
})

watch([qrOpen, isTouchMode], ([open, touch]) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open && touch ? 'hidden' : ''
})

// Header 常驻布局：路由切换时关闭二维码面板，避免 body overflow 残留
watch(() => route.path, () => {
  closeQr()
})

onMounted(() => {
  if (window.matchMedia('(hover: none), (pointer: coarse)').matches) {
    isTouchMode.value = true
  }
  window.addEventListener('touchstart', detectTouchMode, { passive: true })
  window.addEventListener('resize', onResize, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('touchstart', detectTouchMode)
  window.removeEventListener('resize', onResize)
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
})

const changeLang = (langPath: string) => {
  findBuryKey('language')
  safeStorageSetItem('from', 'zh-CN')
  window.location.href = langPath
}
</script>

<style scoped lang="scss">
$ease-out: cubic-bezier(0.22, 1, 0.36, 1);
$panel-dur: 0.28s;

@mixin mobile-qr-panel-visible {
  z-index: 20;
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: scaleY(1);
  transition:
    opacity $panel-dur $ease-out,
    transform $panel-dur cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s linear 0s;
}

.view-on-mobile {
  display: inline-flex;
  height: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  overflow: visible;
}

.pill {
  position: relative;
  display: flex;
  align-items: center;
  height: 38px;
  padding: 8px 12px;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.22s ease;

  >* {
    position: relative;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: rgba(255, 255, 255, 0.1);
    opacity: 0;
    transition: opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
    z-index: 0;
  }

  &:hover::after,
  &.pill--open::after {
    opacity: 1;
  }
}

.view-on-mobile .pill:not(.pill--lang).pill--open {
  z-index: 50;
}

.pill--lang {
  gap: 0;

  .pill__icons {
    margin-right: 8px;
  }

  .pill__label {
    margin-right: 4px;
  }

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    right: -8px;
    top: 100%;
    height: 16px;
    z-index: 24;
  }

  &:hover,
  &:focus-within {
    z-index: 60;
  }
}

.pill__icons {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.pill-icon-img {
  display: block;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.pill__label {
  color: #fff;
  font-size: 16px;
  font-family: 'PingFang SC', system-ui, sans-serif;
  font-weight: 400;
}

.pill-chevron-wrapper {
  padding: 5.06px 3.54px 6.56px;
}

.pill-chevron {
  display: block;
  width: 8.92px;
  height: 5.38px;
  flex-shrink: 0;
  transition: transform $panel-dur $ease-out;
}

.pill--lang:hover .pill-chevron,
.pill--lang:focus-within .pill-chevron {
  transform: rotate(180deg);
}

.lang-pill-wrapper-mask {
  z-index: 24;
  padding-top: 20px;
  position: absolute;
  right: 0;
  top: 100%;
}

.lang-pill-wrapper {
  --color-neutral-01: #151b29;
  --color-brand-orange: #ff8700;
  --color-white: #fff;

  z-index: 25;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  min-width: 120px;
  background: var(--color-neutral-01);
  border-radius: 12px;
  box-shadow: var(--layer-elevated-shadow, 0 0 20px rgba(0, 0, 0, 0.3));

  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(-10px) scale(0.5);
  transform-origin: top right;
  transition:
    opacity $panel-dur $ease-out,
    transform $panel-dur $ease-out,
    visibility 0s linear $panel-dur;
}

.pill--lang:hover .lang-pill-wrapper,
.pill--lang:focus-within .lang-pill-wrapper {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateY(0) scale(1);
  transition:
    opacity $panel-dur $ease-out,
    transform $panel-dur $ease-out,
    visibility 0s linear 0s;
}

.lang-pill-wrapper__row {
  display: inline-flex;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 12px;
  margin: 0;
  border: none;
  background: transparent;
  box-sizing: border-box;
  cursor: pointer;
  font: inherit;
}

.lang-pill-wrapper__row--top {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.lang-pill-wrapper__row--bottom {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.lang-pill-wrapper__row:focus-visible {
  border: 2px solid var(--color-brand-orange);
}

.lang-pill-wrapper__label {
  flex: 1 1 0;
  font-size: 16px;
  font-family: 'PingFang SC', system-ui, sans-serif;
  font-weight: 400;
  color: var(--color-white);
}

.lang-pill-wrapper__row--active .lang-pill-wrapper__label {
  color: var(--color-brand-orange);
}

/* 移动端 Teleport 层：遮罩 + 面板 */
.mobile-qr-layer {
  position: fixed;
  inset: 0;
  z-index: 1998;
  pointer-events: auto;
}

/* 全屏透明遮罩（仅移动端 Teleport） */
.mobile-qr-overlay {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: rgba(0, 0, 0, 0.001);
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
}

.mobile-qr-panel {
  position: absolute;
  right: 0;
  top: 59px;
  display: flex;
  width: 376px;
  padding: 24px 32px;
  flex-direction: row;
  align-items: flex-end;
  gap: 12px;
  justify-content: space-between;
  z-index: 20;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: scaleY(0);
  transform-origin: top right;
  transition:
    opacity $panel-dur $ease-out,
    transform $panel-dur cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s linear $panel-dur;

  --color-white: #fff;
  --color-white-60: rgba(255, 255, 255, 0.6);
  --color-neutral-01: #151b29;
  background: var(--color-neutral-01);
  border-radius: 12px;
  box-shadow: var(--layer-elevated-shadow, 0 0 20px rgba(0, 0, 0, 0.3));

  &--fixed {
    position: fixed;
    z-index: 1999;
  }

  &--visible {
    @include mobile-qr-panel-visible;
  }

  :deep(.top-rectangle) {
    position: absolute;
    top: -6px;
    right: 67px;
    width: 12px;
    height: 6px;
    flex-shrink: 0;
    z-index: 20;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  :deep(.mobile-qr-panel__device) {
    display: block;
    width: calc(173 / 2) * 1px;
    height: calc(356 / 2) * 1px;
    flex-shrink: 0;
    align-self: center;
    border-radius: 7.23px;
    object-fit: cover;
  }

  :deep(.mobile-qr-panel__card) {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
    border-radius: 12px;
  }

  :deep(.mobile-qr-panel__copy) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    align-self: stretch;
    gap: 4px;
  }

  :deep(.mobile-qr-panel__title) {
    align-self: stretch;
    margin: 0;
    color: var(--color-white);
    font-size: 16px;
    font-family: 'PingFang SC', system-ui, sans-serif;
    font-weight: 500;
    line-height: 24px;
    text-align: right;
  }

  :deep(.mobile-qr-panel__desc) {
    width: 195px;
    min-height: 48px;
    margin: 0;
    color: var(--color-white-60);
    font-size: 16px;
    font-family: 'PingFang SC', system-ui, sans-serif;
    font-weight: 400;
    line-height: 22px;
    text-align: right;
    margin-bottom: 12px;
  }

  :deep(.mobile-qr-panel__qr) {
    display: block;
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    object-fit: cover;
  }
}

@media screen and (max-width: 767px) {
  .view-on-mobile {
    .pill {
      border: none;
      outline: none;
      background: none;
      padding: 0;

      &::after {
        content: '';
        display: none;
      }
    }

    .pill__icons {
      margin-right: 0;
    }

    .pill__label {
      display: none;
    }

    .pill-chevron-wrapper {
      display: none;
    }
  }

  .mobile-qr-panel {
    right: -30px;

    :deep(.top-rectangle) {
      right: 32px;
    }
  }
}

@media screen and (max-width: 499px) {
  .mobile-qr-panel {
    right: -30px;

    :deep(.top-rectangle) {
      right: 42px;
    }
  }
}
</style>
